/**
 * Wires up renderer-side handlers for named pipe methods and exposes
 * `window.__evalWorld` for live scripting from the browser console.
 * Call `installEditorBridge()` once during editor initialisation.
 */

'use client';

import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import { editorStore } from '../store';

import type { AssetType } from '@/editor/core/assetTypes';
import type { IActorSpawnTransform, IAssetBounds, IEditorAssetType, IEditorContext, IOutlinerNode } from '@gnsx/genesys.js';

import { handleEditorApiQuery } from '@/app/editor/api/editor-api';
import { sdk } from '@/app/sdk';
import { nameHelpers } from '@/editor/core';
import { ensurePrefabAsset, loadPrefabAsset } from '@/editor/core/actions/prefabActions';

export function createEditorContext(): IEditorContext {
  return {
    getWorld() {
      return editorStore.scene.world ?? null;
    },

    addActorToWorld(actor: ENGINE.Actor) {
      const world = editorStore.scene.world;
      if (!world) return null;
      world.addActor(actor);
      editorStore.scene.updateOutliner();
      const root = editorStore.scene.outliner.rootNode ?? undefined;
      const unique = nameHelpers.assignUniqueActorDisplayName(actor, root, { omitNodeId: actor.uuid });
      editorStore.scene.outliner.getNodeById(actor.uuid)?.setName(unique);
      return actor;
    },

    async addPrefabToWorld(prefabPath: string, transform?: IActorSpawnTransform, name?: string) {
      const world = editorStore.scene.world;
      if (!world) return null;

      const loaded = await ensurePrefabAsset(prefabPath);
      if (!loaded) return null;

      const position = transform?.position
        ? new THREE.Vector3(transform.position.x, transform.position.y, transform.position.z)
        : undefined;
      const rotation = transform?.rotation
        ? new THREE.Euler(transform.rotation.x, transform.rotation.y, transform.rotation.z)
        : undefined;
      const scale = transform?.scale
        ? new THREE.Vector3(transform.scale.x, transform.scale.y, transform.scale.z)
        : undefined;

      const actors = await ENGINE.WorldCommands.placePrefabs({
        world,
        prefabs: [{ path: prefabPath, transform: { position, rotation, scale } }],
      });

      const actor = actors?.[0] ?? null;
      editorStore.scene.updateOutliner();
      if (actor) {
        const root = editorStore.scene.outliner.rootNode ?? undefined;
        if (name) {
          const existing = nameHelpers.collectOutlinerNames(root, { omitNodeId: actor.uuid });
          const unique = nameHelpers.makeUniqueName(name, existing);
          nameHelpers.setCustomDisplayName(actor, unique);
        } else {
          nameHelpers.assignUniqueActorDisplayName(actor, root, { omitNodeId: actor.uuid, assetPathHint: prefabPath });
        }
        editorStore.scene.outliner.getNodeById(actor.uuid)?.setName(actor.name);
      }
      return actor;
    },

    deleteActors(...actors: ENGINE.Actor[]) {
      editorStore.scene.removeObjects(...actors);
    },

    getSelectedActor() {
      return editorStore.scene.selectedActors[0] ?? null;
    },

    listActors() {
      return editorStore.scene.getSceneActors();
    },

    getActorByName(name: string) {
      return editorStore.scene.world?.getActorByName(name) ?? null;
    },

    getActorByUuid(uuid: string) {
      return editorStore.scene.world?.getActorByUuid(uuid) ?? null;
    },

    getComponentByUuid(uuid: string) {
      const world = editorStore.scene.world;
      if (!world) return null;
      for (const actor of world.getActors()) {
        const component = actor.getComponents(ENGINE.SceneComponent).find(c => c.uuid === uuid);
        if (component) return component;
      }
      return null;
    },

    async getAssetBounds(assetPath: string): Promise<IAssetBounds | null> {
      let object: THREE.Object3D | null = null;

      if (assetPath.endsWith(ENGINE.PREFAB_EXTENSION)) {
        const loaded = await ensurePrefabAsset(assetPath);
        if (!loaded) return null;
        const actor = loadPrefabAsset(assetPath);
        if (!actor) return null;
        // Wait up to 3 s for any GLTF sub-components to finish loading.
        const startTime = Date.now();
        await new Promise<void>(resolve => {
          const check = () => {
            const gltfComponents = actor.getComponents(ENGINE.ModelMeshComponent);
            const allLoaded = gltfComponents.length === 0 ||
              gltfComponents.every(c => { const m = c.getModel(); return m && m.children.length > 0; });
            if (allLoaded || Date.now() - startTime > 3000) resolve();
            else setTimeout(check, 16);
          };
          check();
        });
        object = actor.rootComponent;
      } else {
        const gltf = await ENGINE.resourceManager.loadModel(ENGINE.AssetPath.fromString(assetPath));
        if (!gltf) return null;
        object = gltf.scene;
      }

      const box = new THREE.Box3().setFromObject(object);
      if (box.isEmpty()) return null;

      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      return {
        size:   { x: size.x,       y: size.y,       z: size.z },
        center: { x: center.x,     y: center.y,     z: center.z },
        min:    { x: box.min.x,    y: box.min.y,    z: box.min.z },
        max:    { x: box.max.x,    y: box.max.y,    z: box.max.z },
      };
    },

    listAssets(type: IEditorAssetType) {
      return editorStore.assets.getAssetSelectOptions(type as AssetType);
    },

    ensureFolder(name: string, parentId?: string) {
      const outliner = editorStore.scene.outliner;
      const parentNode = parentId ? outliner.getNodeById(parentId) : outliner.rootNode;
      if (!parentNode) return null;

      const existing = parentNode.children.find(
        (child) => child.nodeType === 'folder' && child.name === name,
      );
      if (existing) return existing.id;

      const created = outliner.createFolder(name, undefined, parentNode);
      return created?.id ?? null;
    },

    renameFolder(folderId: string, newName: string) {
      const outliner = editorStore.scene.outliner;
      const folder = outliner.getNodeById(folderId);
      if (!folder?.isFolder) return false;
      outliner.renameFolder(folderId, newName);
      return true;
    },

    deleteFolder(folderId: string, deleteContents: boolean = true) {
      const outliner = editorStore.scene.outliner;
      const folder = outliner.getNodeById(folderId);
      if (!folder?.isFolder) return false;
      outliner.removeFolder(folderId, !deleteContents);
      return true;
    },

    reparentActor(actorId: string, targetId: string | null) {
      const outliner = editorStore.scene.outliner;

      const actorNode = outliner.getNodeById(actorId);
      if (!actorNode?.isActorNode) {
        console.warn(`[EditorContext] reparentActor: actor ${actorId} not found`);
        return false;
      }

      const targetNode = targetId === null ? outliner.rootNode : outliner.getNodeById(targetId);
      if (!targetNode) {
        console.warn(`[EditorContext] reparentActor: target ${targetId} not found`);
        return false;
      }
      if (!targetNode.isFolder && !targetNode.isScene) {
        console.warn(`[EditorContext] reparentActor: target ${targetId} is not a folder or scene root`);
        return false;
      }

      outliner.reparentNode(actorId, targetNode.id);
      return true;
    },

    getOutlinerState(): IOutlinerNode[] {
      const outliner = editorStore.scene.outliner;
      const rootNode = outliner.rootNode;
      if (!rootNode) return [];

      const nodes: IOutlinerNode[] = [];

      const traverse = (node: any, parentId: string | null) => {
        nodes.push({
          id: node.id,
          name: node.name,
          nodeType: node.nodeType,
          parentId,
          childIds: node.children.map((c: any) => c.id),
        });
        for (const child of node.children) {
          traverse(child, node.id);
        }
      };

      traverse(rootNode, null);
      return nodes;
    },
  };
}

// Objects with a `describe()` method (Actor, SceneComponent, World, ...) are
// serialized via that method. Arrays are handled element-by-element. Everything
// else is passed straight to JSON.stringify.
function serializeEvalResult(value: unknown): string {
  if (Array.isArray(value)) {
    return JSON.stringify(value.map(item => serializeItem(item)));
  }
  return JSON.stringify(serializeItem(value));
}

function serializeItem(value: unknown): unknown {
  if (value !== null && typeof value === 'object' && typeof (value as any).describe === 'function') {
    return (value as any).describe();
  }
  return value;
}

async function evalWorld(script: string): Promise<string> {
  const world = editorStore.scene.world;
  if (!world) throw new Error('No active world.');

  const editor = createEditorContext();

  const contextKeys = ['world', 'sceneStore', 'editorStore', 'editor', 'ENGINE', 'THREE'] as const;
  const contextValues = [world, editorStore.scene, editorStore, editor, ENGINE, THREE];

  const fn = new Function(...contextKeys, `"use strict"; return (async () => { ${script} })();`);
  const result = await fn(...contextValues);
  return serializeEvalResult(result);
}

if (typeof window !== 'undefined') {
  (window as any).__evalWorld = evalWorld;
}

let editorBridgeInstalled = false;

/** Installs all pipe query handlers. Idempotent: extra calls are no-ops once registered. */
export function installEditorBridge(): void {
  if (editorBridgeInstalled) return;
  if (!sdk?.pipe) return;
  editorBridgeInstalled = true;

  // ── editor/eval-world ────────────────────────────────────────────────────────
  // Params: { script: string }
  // Runs a script in the renderer context inside an async function; use `return`
  // to produce a value. The result is JSON-serialized before being sent back.
  //
  // Available globals:
  //   - `editor`       — IEditorContext adapter (preferred API for agents)
  //   - `world`        — the active ENGINE.World (throws if none is loaded)
  //   - `sceneStore`   — the editor's scene store
  //   - `editorStore`  — the top-level editor store
  //   - `ENGINE`       — the full @gnsx/genesys.js namespace
  //   - `THREE`        — the full three namespace

  sdk.pipe.registerQueryHandler('editor/eval-world', async (params) => {
    const script = params['script'];
    if (typeof script !== 'string') throw new Error('Missing required param: script');
    return evalWorld(script);
  });

  const editorApiMethods = [
    'editor-api/getSceneGraph',
    'editor-api/findActors',
    'editor-api/getSelectedActors',
    'editor-api/getActorDetails',
    'editor-api/getEditorState',
    'editor-api/getActiveScenePath',
    'editor-api/getSceneSummary',
    'editor-api/getBuildErrors',
    'editor-api/getConsoleErrors',
    'editor-api/getGeometryCatalog',
    'editor-api/undoGroup',
    'editor-api/actionActor',
    'editor-api/actionComponent',
    'editor-api/actionEditor',
    'editor-api/actionScene',
    'editor-api/actionPrefab',
  ] as const;

  for (const method of editorApiMethods) {
    sdk.pipe.registerQueryHandler(method, async (params) => {
      return handleEditorApiQuery(method, params);
    });
  }
}
